import { Activity, ArrowRight, Droplets, Wrench, Zap } from "lucide-react";
import { useParams } from "react-router-dom";
import { Seo } from "../components/layout/Seo";
import { Button } from "../components/ui/Button";
import { isLocale, localizedPath } from "../lib/routes";

const sec = "py-[clamp(40px,5vw,48px)]";
const scrollMt = "scroll-mt-28";

const startingPoints: Array<[string, string, typeof Zap]> = [
  ["ENERGY COSTS", "High or unpredictable electricity expenditure.", Zap],
  ["RELIABILITY", "Outages, grid constraints or critical processes that cannot stop.", Activity],
  ["CLIMATE & RESOURCES", "Water, heat, irrigation or climate-related operating pressure.", Droplets],
  ["MODERNIZATION", "Efficiency, sustainability or market requirements driving new investment.", Wrench]
];

const logicStages: Array<[string, string, string, string[], string]> = [
  ["01", "OPERATING PRESSURE", "What is creating the need to act?", ["Energy cost", "Reliability", "Water & climate", "Modernization"], "border-brand-blue"],
  ["02", "SITE REALITY", "What is actually happening on the ground?", ["Loads & usage", "Processes", "Existing assets", "Operating constraints"], "border-brand-cyan"],
  ["03", "FEASIBILITY", "Is there a credible route to implementation?", ["Technical fit", "Economics", "Regulation", "Financing context"], "border-brand-green"]
];

const screeningInputs = [
  ["01", "ENERGY PROFILE", "How much energy is used, when it is used and where the main loads occur."],
  ["02", "OPERATIONAL CONSTRAINTS", "Outages, peak loads, production bottlenecks or processes that require continuous operation."],
  ["03", "EXISTING ASSETS", "Solar PV, generators, storage, cooling, pumps, major equipment or other relevant infrastructure."],
  ["04", "BUSINESS OBJECTIVE", "Cost reduction, resilience, modernization, sustainability, capacity growth or another operational goal."],
  ["05", "INVESTMENT HORIZON", "Whether the organization is actively planning an investment or is still exploring options."]
];

const operatingContexts = [
  ["FARMS, VINEYARDS & GREENHOUSES", "Irrigation, pumps, water management, agrivoltaics and climate resilience.", "border-brand-green"],
  ["WINERIES & FOOD PROCESSING", "Refrigeration, process energy, cold storage, water use and operational continuity.", "border-brand-cyan"],
  ["COMMERCIAL & PUBLIC FACILITIES", "Energy cost, reliability, modernization, existing solar assets and critical loads.", "border-brand-blue"]
];

const developmentSteps = [
  ["01", "Screen", "Is there a credible operational problem and enough evidence to investigate it?"],
  ["02", "Feasibility", "Test technical fit, economics, regulation and implementation constraints."],
  ["03", "Project design", "Define the site, technology pathway, partners and financing logic."],
  ["04", "Pilot", "Implement at a limited, measurable real-world scale."],
  ["05", "Evidence", "Measure technical performance, economics and practical constraints."],
  ["06", "Replication", "Use the evidence to determine whether and how the model should scale."]
];

const financingMechanisms = ["Public support programmes", "EU-backed mechanisms", "Green loans", "Guarantees", "Grants", "Investment incentives"];

const whyPrinciples = [
  ["PROBLEM FIRST", "We start from the operating constraint."],
  ["TECHNOLOGY-NEUTRAL", "Technology is considered after the problem and feasibility are understood."],
  ["MOLDOVA + ISRAEL", "Moldovan site context connects with Israeli technology and operating expertise."],
  ["PILOT BEFORE SCALE", "Real-world evidence comes before replication."]
];

const afterSubmit: Array<[string, string, string]> = [
  ["01", "REVIEW", "We review the information and identify major gaps or questions."],
  ["02", "CLARIFY", "If needed, we request additional information or suggest a short discussion."],
  ["03", "RECOMMEND", "We tell you whether further feasibility work appears justified and what the next step may be."]
];

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function Moldova() {
  const { locale } = useParams();
  const activeLocale = isLocale(locale) ? locale : "en";
  const screeningHref = localizedPath(activeLocale, "moldova/screening");

  return (
    <>
      <Seo title="Moldova" description="TransitionBridge helps organizations in Moldova assess energy resilience, climate, modernization and implementation opportunities before moving into feasibility, financing and pilot development." />

      {/* HERO */}
      <section className="grid-overlay overflow-hidden pt-[calc(var(--header-height)+30px)]">
        <div className="container-grid grid items-center gap-12 pb-10 lg:grid-cols-[1fr_0.95fr] lg:gap-14">
          <div className="max-w-[700px]">
            <p className="eyebrow mb-5">FOR ORGANIZATIONS IN MOLDOVA</p>
            <p className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink-secondary">
              <span className="h-px w-8 bg-brand-blue/50" aria-hidden="true" />
              Energy · Climate · Modernization
            </p>
            <h1 className="text-balance text-[clamp(2rem,5vw,3.2rem)] font-extrabold leading-[1.04]">
              <span className="block">Reduce energy risk.</span>
              <span className="block">Build a more resilient business.</span>
            </h1>
            <p className="mt-6 max-w-[620px] text-lg leading-8 text-ink-secondary md:text-xl">
              We help businesses, producers and communities in Moldova assess energy, climate and modernization challenges and determine whether there is a credible basis for an implementable project.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button to={screeningHref}>Request a Project Screening</Button>
              <Button type="button" variant="secondary" onClick={() => scrollToId("project-logic")}>See how it works</Button>
            </div>
          </div>

          <div className="w-full">
            <div className="mb-5 flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-ink-secondary">Typical starting points</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {startingPoints.map(([title, body, Icon], index) => (
                <div key={title} className="rounded-2xl border border-line bg-surface/70 p-5 transition-colors duration-200 hover:border-brand-blue/30 hover:bg-surface">
                  <div className="mb-5 flex items-center justify-between">
                    <span className="grid h-10 w-10 place-items-center rounded-xl border border-brand-blue/15 bg-brand-blue/[0.06] text-brand-blue">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="font-display text-sm font-bold text-brand-blue/60">{String(index + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mb-2 text-sm font-extrabold uppercase tracking-wide">{title}</h3>
                  <p className="text-sm leading-6 text-ink-secondary">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROJECT LOGIC */}
      <section id="project-logic" className={`${scrollMt} bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">PROJECT LOGIC</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">From operating pressure to a project worth building</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">We begin with the operating reality, not with a preferred technology. Each stage narrows the question until there is enough evidence to either stop early or move toward a pilot.</p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-stretch">
            {logicStages.flatMap(([num, title, question, items, border], i) => {
              const stage = (
                <div key={num} className={`border-t-2 ${border} pt-5`}>
                  <p className="mb-2 text-xs font-bold text-brand-blue/60">{num}</p>
                  <h3 className="text-lg font-extrabold uppercase tracking-tight">{title}</h3>
                  <p className="mt-2 text-sm font-semibold text-ink-primary">{question}</p>
                  <ul className="mt-3 grid gap-1.5 text-sm leading-6 text-ink-secondary">
                    {(items as string[]).map((item) => <li key={item} className="flex items-start gap-2"><span className="mt-2 block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan/70" aria-hidden="true" />{item}</li>)}
                  </ul>
                </div>
              );
              if (i === logicStages.length - 1) return [stage];
              return [stage, <div key={`${num}-arrow`} className="flex items-center justify-center text-brand-cyan"><ArrowRight className="rtl-flip h-5 w-5 rotate-90 md:rotate-0" aria-hidden="true" /></div>];
            })}
          </div>

          {/* connector: feasibility (03) → decision gate */}
          <div className="relative mx-auto mt-8 hidden h-12 w-full md:block" aria-hidden="true">
            <div className="absolute left-[85%] top-0 h-6 w-px bg-brand-cyan/50" />
            <div className="absolute left-1/2 right-[15%] top-6 h-px bg-brand-cyan/50" />
            <div className="absolute left-1/2 top-6 h-6 w-px -translate-x-1/2 bg-brand-cyan/50" />
          </div>
          <div className="mt-6 flex justify-center md:mt-0">
            <div className="max-w-md rounded-2xl bg-brand-dark px-7 py-5 text-center text-brand-darkText">
              <p className="eyebrow-sage mb-2">DECISION GATE</p>
              <p className="text-lg font-bold">Is there enough evidence to continue?</p>
            </div>
          </div>

          {/* branching connectors: gate → two outcomes */}
          <div className="relative mx-auto hidden h-20 w-full max-w-4xl md:block" aria-hidden="true">
            <div className="absolute left-1/2 top-0 h-10 w-px -translate-x-1/2 bg-brand-cyan/50" />
            <div className="absolute left-1/4 right-1/4 top-10 h-px bg-brand-cyan/50" />
            <div className="absolute left-1/4 top-10 h-10 w-px -translate-x-1/2 bg-brand-cyan/50" />
            <div className="absolute right-1/4 top-10 h-10 w-px translate-x-1/2 bg-brand-cyan/50" />
          </div>

          <div className="mt-8 grid gap-5 md:mt-0 md:grid-cols-2">
            <div className="order-2 md:order-1">
              <p className="mb-3 text-center text-xs font-bold uppercase tracking-[0.14em] text-ink-secondary">Not enough evidence</p>
              <div className="rounded-[15px] border border-line bg-bg-secondary p-5">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-secondary">Valid outcome</p>
                <h3 className="mt-2 text-xl font-bold">Stop early</h3>
                <p className="mt-2 leading-7 text-ink-secondary">The case is too weak, premature or poorly matched to justify deeper work.</p>
                <p className="mt-2 text-sm font-semibold text-ink-secondary">Avoid unnecessary cost and complexity.</p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <p className="mb-3 text-center text-xs font-bold uppercase tracking-[0.14em] text-brand-green">Credible basis</p>
              <div className="rounded-[15px] border border-brand-green/25 bg-brand-green/[0.06] p-5">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-green">Next stage</p>
                <h3 className="mt-2 text-xl font-bold">Build toward a pilot</h3>
                <p className="mt-2 leading-7 text-ink-secondary">The evidence supports deeper feasibility, project design and partner selection.</p>
                <p className="mt-2 text-sm font-semibold text-ink-secondary">Move forward with clear questions and measurable assumptions.</p>
              </div>
            </div>
          </div>

          <p className="mt-8 max-w-3xl border-l-4 border-brand-blue ps-5 leading-7 text-ink-secondary">A useful screening can end with “not yet” or “not viable”. Avoiding a weak project is also a good outcome.</p>
        </div>
      </section>

      {/* SCREENING INPUTS */}
      <section id="screening-inputs" className={`${scrollMt} bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">PROJECT SCREENING</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">What do we need to understand first?</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">A useful assessment starts with a small number of practical inputs. Exact engineering data is not required at the first stage.</p>
          </div>
          <div className="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2">
            {screeningInputs.map(([num, title, body], i) => (
              <div key={num} className={`border-t border-line pt-5 ${i === 4 ? "md:col-span-2" : ""}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-bold text-brand-blue/60">{num}</span>
                  <h3 className="text-base font-extrabold uppercase tracking-tight">{title}</h3>
                </div>
                <p className="mt-2 max-w-2xl leading-7 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 border-l-2 border-brand-blue ps-4 text-base font-medium text-ink-primary">These inputs help determine whether further feasibility work makes sense.</p>
        </div>
      </section>

      {/* OPERATING CONTEXTS */}
      <section id="operating-contexts" className={`${scrollMt} bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">OPERATING CONTEXTS</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Where these challenges become real</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Different sites experience energy, water, climate and modernization pressure in different ways. We assess the operating context before considering specific technologies.</p>
          </div>
          <div className="mt-10">
            <img src="/assets/moldova/moldova-operating-contexts.webp" alt="Moldovan farms, wineries, food-processing facilities and public buildings connected through energy, water and resilience solutions." width={1935} height={812} loading="lazy" decoding="async" className="w-full rounded-3xl border border-line object-contain" />
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-3 md:gap-10">
            {operatingContexts.map(([title, body, border]) => (
              <div key={title} className={`border-t-2 ${border} pt-5`}>
                <h3 className="text-base font-extrabold uppercase tracking-tight">{title}</h3>
                <p className="mt-3 leading-7 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT DEVELOPMENT PATH */}
      <section id="project-development" className={`${scrollMt} bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">IF THE CASE IS CREDIBLE</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">How a credible case becomes a working project</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Screening is only the first gate. If the evidence supports further work, the project is developed in stages.</p>
          </div>

          <div className="relative mt-12 hidden lg:block">
            <div className="absolute left-0 right-0 top-4 h-px bg-[linear-gradient(90deg,rgba(36,87,245,0.45),rgba(18,175,203,0.45))]" aria-hidden="true" />
            <ol className="grid grid-cols-6 gap-5">
              {developmentSteps.map(([num, title, body], i) => {
                const pivot = num === "01";
                return (
                  <li key={num} className="relative">
                    <span className={`absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${pivot ? "bg-brand-blue text-white shadow-[0_0_0_4px_rgba(36,87,245,0.14)]" : "border border-brand-cyan/50 bg-bg-secondary text-brand-cyanDark"}`} aria-hidden="true">{num}</span>
                    <div className="pt-12">
                      <h3 className={`text-[1.0625rem] font-semibold ${pivot ? "text-brand-blue" : "text-ink-primary"}`}>{title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
                      {pivot && (
                        <div className="mt-4 border-l-2 border-brand-cyan/40 ps-3">
                          <p className="text-[0.7rem] font-bold uppercase tracking-[0.12em] text-ink-secondary">No credible case</p>
                          <p className="text-sm font-semibold text-ink-primary">Stop early</p>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="relative mt-10 lg:hidden">
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-[linear-gradient(180deg,rgba(36,87,245,0.45),rgba(18,175,203,0.45))]" aria-hidden="true" />
            <ol className="grid gap-7">
              {developmentSteps.map(([num, title, body]) => (
                <li key={num} className="relative ps-12">
                  <span className="absolute start-0 top-0 grid h-8 w-8 place-items-center rounded-full border border-brand-cyan/50 bg-bg-secondary text-xs font-bold text-brand-cyanDark" aria-hidden="true">{num}</span>
                  <h3 className="text-[1.0625rem] font-semibold">{title}</h3>
                  <p className="mt-1.5 text-sm leading-6 text-ink-secondary">{body}</p>
                  {num === "01" && (
                    <div className="mt-3 border-l-2 border-brand-cyan/40 ps-3">
                      <p className="text-[0.7rem] font-bold uppercase tracking-[0.12em] text-ink-secondary">No credible case</p>
                      <p className="text-sm font-semibold text-ink-primary">Stop early</p>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* FINANCING */}
      <section className="petrol-section">
        <div className="container-grid py-[clamp(40px,5vw,48px)]">
          <div className="max-w-[760px]">
            <p className="eyebrow-sage mb-4">FINANCING</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] text-brand-darkText md:text-[2.6rem]">Financing follows feasibility.</h2>
            <p className="mt-5 text-lg leading-8 text-brand-darkMuted">We first test the operational need, available data and project economics. If there is a credible investment case, we then identify financing pathways that may be relevant to the applicant and project.</p>
          </div>
          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
            <div>
              <p className="text-2xl font-bold text-brand-darkText">Need + Evidence + Economics → Financing</p>
              <p className="mt-4 max-w-md leading-7 text-brand-darkMuted">Funding is matched to a credible project case. It is not assumed in advance.</p>
            </div>
            <div>
              <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-darkMuted">Pathways that may be relevant</p>
              <ul className="grid gap-3 sm:grid-cols-2">
                {financingMechanisms.map((item) => (
                  <li key={item} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-brand-darkText">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-sm leading-6 text-brand-darkText/80">Eligibility, funding level and financing conditions depend on the applicant, programme and individual project. No funding outcome is guaranteed.</p>
        </div>
      </section>

      {/* WHY TRANSITIONBRIDGE */}
      <section className={`bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">OUR APPROACH</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">A bridge only matters if it reduces implementation friction.</h2>
          </div>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 md:grid-cols-4 md:gap-8">
            {whyPrinciples.map(([title, body]) => (
              <div key={title} className="border-t-2 border-brand-cyan/40 pt-5">
                <h3 className="text-sm font-extrabold uppercase tracking-[0.12em]">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AFTER YOU SUBMIT (compact) */}
      <section className={`bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <p className="eyebrow mb-4">AFTER YOU SUBMIT</p>
          <p className="max-w-[760px] text-lg leading-8 text-ink-secondary">A screening does not automatically lead to a project.</p>
          <ol className="mt-8 grid gap-6 sm:grid-cols-3">
            {afterSubmit.map(([num, title, body]) => (
              <li key={title}>
                <h3 className="text-sm font-extrabold uppercase tracking-[0.12em] text-brand-blue">{num} {title}</h3>
                <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FINAL SCREENING CTA */}
      <section className="bg-bg-primary">
        <div className="container-grid">
          <div className="h-px w-full bg-[linear-gradient(90deg,#2457f5,#12afcb,#28c98b)]" aria-hidden="true" />
        </div>
        <div className="container-grid py-[clamp(44px,5vw,56px)]">
          <div className="max-w-[820px]">
            <p className="eyebrow mb-4">PROJECT SCREENING</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Is there enough here to build a real project?</h2>
            <p className="mt-5 max-w-[720px] text-lg leading-8 text-ink-secondary">A short screening helps us understand the operating problem, available evidence and whether deeper feasibility work is justified.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {[["≈ 4–6 MINUTES", "to complete"], ["NO ENGINEERING PACKAGE REQUIRED", "Approximate data is enough to begin"], ["NO COMMITMENT", "Screening may conclude that no project is justified"]].map(([title, sub]) => (
                <div key={title} className="border-t border-line pt-4">
                  <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-ink-primary">{title}</p>
                  <p className="mt-1 text-sm leading-6 text-ink-secondary">{sub}</p>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Button to={screeningHref}>Start Project Screening</Button>
              <Button variant="secondary" to={localizedPath(activeLocale, "contact")}>Prefer to talk first? Start a conversation</Button>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-bg-primary/95 p-3 backdrop-blur md:hidden">
        <Button className="w-full" to={screeningHref}>Request a Project Screening</Button>
      </div>
    </>
  );
}
